import type { AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { toast } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import { BreadcrumbTrail } from "./components/BreadcrumbTrail";
import { ChartCanvas } from "./components/ChartCanvas";
import { ChartToolbar } from "./components/ChartToolbar";
import { Minimap } from "./components/Minimap";
import { SearchPanel } from "./components/SearchPanel";
import { ZoomControls } from "./components/ZoomControls";

import { useSuppressFeedbackButtons } from "../../containers/FeedbackDrawers/useSuppressFeedbackButtons";

import type { OrgEmployee } from "./types";

import { useRbacPrimaryRolePermissions } from "../../api/hooks";
import { useOrgChart } from "../../api/hooks/defs/org-chart";
import { useChartExpansion } from "./useChartExpansion";
import { useChartFocus } from "./useChartFocus";
import { useChartNavigation } from "./useChartNavigation";
import { usePageHeight } from "./usePageHeight";
import { useViewport } from "./useViewport";
import { useViewportDimensions } from "./useViewportDimensions";

import { appendCurrentLocationAsFrom } from "../../utils/changeFieldsBreadcrumbs";

import ErrorPage from "../Error";
import css from "./OrgChart.module.scss";
import { processOrgData } from "./data";
import { buildChildrenMap, buildParentMap, computeVisibleData } from "./expandCollapse";
import { exportToPng } from "./export";
import { LayoutNode, computeBounds, computeTreeLayout, orderNodeIdsForTreeLayout } from "./layout";

function sanitizeFilename(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "");
}

function getReportingChain(employeeId: string, employees: OrgEmployee[]): OrgEmployee[] {
	const employeeMap = new Map(employees.map((employee) => [employee.id, employee]));
	const chain: OrgEmployee[] = [];
	const visited = new Set<string>();
	let currentId: string | null = employeeId;

	while (currentId && employeeMap.has(currentId) && !visited.has(currentId)) {
		visited.add(currentId);
		const employee = employeeMap.get(currentId)!;
		chain.unshift(employee);
		currentId = employee.managerId;
	}

	return chain;
}

type OrgChartProps = {
	bottomOffset?: number;
};

export function OrgChart({ bottomOffset = 0 }: OrgChartProps) {
	// The old standalone route carried NO_FEEDBACK. Scope that behavior to this tab so
	// floating feedback actions do not cover the minimap while Directory remains unchanged.
	useSuppressFeedbackButtons(true);
	const { data, isLoading, isError, error } = useOrgChart();
	const { data: permissions } = useRbacPrimaryRolePermissions("company_settings");
	const [searchParams, setSearchParams] = useSearchParams();
	const employeeIdFromUrl = searchParams.get("employeeId");
	const { pageRef, pageHeight } = usePageHeight({ bottomOffset });
	const canChangeFields = !!permissions?.includes("company_settings:editSettings");

	const fullOrgData = useMemo(() => processOrgData(data?.employees ?? []), [data?.employees]);
	const childrenMap = useMemo(() => buildChildrenMap(fullOrgData), [fullOrgData]);
	const parentMap = useMemo(() => buildParentMap(fullOrgData), [fullOrgData]);
	const employeeMap = useMemo(() => {
		return new Map(fullOrgData.employees.map((employee) => [employee.id, employee]));
	}, [fullOrgData.employees]);

	const missingEmployeeId =
		employeeIdFromUrl && fullOrgData.employees.length > 0 && !employeeMap.has(employeeIdFromUrl)
			? employeeIdFromUrl
			: null;

	const viewportRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const handleViewportTap = useCallback((event: React.PointerEvent) => {
		const target = event.target as HTMLElement | null;
		if (target?.closest("[data-interactive]")) return;
		clearFocusRef.current?.();
	}, []);
	const clearFocusRef = useRef<(() => void) | null>(null);
	const { transform, fitView, centerOn, zoomTo, setView, wasDrag, handlers } = useViewport({
		viewportRef,
		contentRef,
		onTap: handleViewportTap,
	});

	const navTargetRef = useRef<string | null>(null);
	const anchorRef = useRef<{
		nodeId: string;
		screenX: number;
		screenY: number;
	} | null>(null);
	const fitOnNextRef = useRef(true);

	const { expandedIds, setExpandedIds, expandToNode, expandAll, collapseAll } = useChartExpansion({
		fullOrgData,
		childrenMap,
		parentMap,
		fitOnNextRef,
	});

	const { orgData: visibleOrgData, nodeInfo } = useMemo(() => {
		return computeVisibleData(fullOrgData, childrenMap, expandedIds);
	}, [fullOrgData, childrenMap, expandedIds]);

	const positions = useMemo(() => {
		const nodeIds = orderNodeIdsForTreeLayout(
			visibleOrgData.employees.map((employee) => employee.id),
			visibleOrgData.roots,
			visibleOrgData.cycleGroups
		);

		return computeTreeLayout(nodeIds, visibleOrgData.edges, visibleOrgData.roots);
	}, [visibleOrgData.employees, visibleOrgData.edges, visibleOrgData.roots, visibleOrgData.cycleGroups]);

	const bounds = useMemo(() => computeBounds(positions), [positions]);
	const offsetX = -bounds.minX + 50;
	const offsetY = -bounds.minY + 50;
	const contentWidth = Math.max(bounds.width + 100, 100);
	const contentHeight = Math.max(bounds.height + 100, 100);

	const layoutNodes = useMemo(
		() =>
			visibleOrgData.employees
				.map((employee) => positions.get(employee.id))
				.filter((position): position is LayoutNode => !!position)
				.map((position) => ({
					...position,
					x: position.x + offsetX,
					y: position.y + offsetY,
				})),
		[visibleOrgData.employees, positions, offsetX, offsetY]
	);

	const viewportDimensions = useViewportDimensions(viewportRef);

	const { selectedNodeId, highlightedNodeId, focusEmployee, clearFocus, handleNodeClick } = useChartFocus({
		employeeMap,
		employeeIdFromUrl,
		fullEmployeesLength: fullOrgData.employees.length,
		positions,
		offsetX,
		offsetY,
		centerOn,
		expandToNode,
		wasDrag,
		setSearchParams,
		fitOnNextRef,
		navTargetRef,
	});

	useEffect(() => {
		clearFocusRef.current = clearFocus;
	}, [clearFocus]);

	const toggleNode = useCallback(
		(nodeId: string) => {
			const position = positions.get(nodeId);
			if (position) {
				const contentX = position.x + offsetX + position.width / 2;
				const contentY = position.y + offsetY + position.height / 2;
				anchorRef.current = {
					nodeId,
					screenX: contentX * transform.scale + transform.x,
					screenY: contentY * transform.scale + transform.y,
				};
			}

			setExpandedIds((previous) => {
				const next = new Set(previous);
				if (next.has(nodeId)) {
					next.delete(nodeId);
				} else {
					next.add(nodeId);
				}
				return next;
			});
		},
		[offsetX, offsetY, positions, setExpandedIds, transform.scale, transform.x, transform.y]
	);

	useChartNavigation({
		positions,
		layoutNodes,
		offsetX,
		offsetY,
		transform,
		centerOn,
		fitView,
		setView,
		navTargetRef,
		anchorRef,
		fitOnNextRef,
	});

	const reportingChain = useMemo(() => {
		if (!selectedNodeId) return [];
		return getReportingChain(selectedNodeId, fullOrgData.employees);
	}, [fullOrgData.employees, selectedNodeId]);

	const visibleCycleGroups = useMemo(() => {
		const visibleIds = new Set(visibleOrgData.employees.map((employee) => employee.id));
		return fullOrgData.cycleGroups.filter((group) => group.memberIds.every((id) => visibleIds.has(id)));
	}, [fullOrgData.cycleGroups, visibleOrgData.employees]);

	const exportRenderData = useMemo(
		() => ({
			visibleEmployees: visibleOrgData.employees,
			positions,
			edges: visibleOrgData.edges,
			cycleGroups: visibleCycleGroups,
			offsetX,
			offsetY,
			contentWidth,
			contentHeight,
		}),
		[
			contentHeight,
			contentWidth,
			offsetX,
			offsetY,
			positions,
			visibleCycleGroups,
			visibleOrgData.edges,
			visibleOrgData.employees,
		]
	);

	const handleExportPng = useCallback(async () => {
		try {
			const baseFilename = `${sanitizeFilename(data?.company.name || "performyard")}-org-chart`;
			await exportToPng({ ...exportRenderData, filename: `${baseFilename}.png` });
			toast.success("Org chart exported successfully");
		} catch (error) {
			console.error("Export PNG failed:", error);
			toast.error("Failed to export org chart. Please try again.");
		}
	}, [data?.company.name, exportRenderData]);

	const handleChangeFields = useCallback(() => {
		window.location.assign(appendCurrentLocationAsFrom("/administration/change-fields/"));
	}, []);

	const handleMinimapNavigate = useCallback(
		(contentX: number, contentY: number) => {
			centerOn(contentX, contentY, transform.scale, 0);
		},
		[centerOn, transform.scale]
	);

	const centerVisibleChart = useCallback(() => {
		fitView(layoutNodes);
	}, [fitView, layoutNodes]);

	const zoomIn = useCallback(() => {
		zoomTo(transform.scale * 1.3);
	}, [transform.scale, zoomTo]);

	const zoomOut = useCallback(() => {
		zoomTo(transform.scale / 1.3);
	}, [transform.scale, zoomTo]);

	const pageStyle = pageHeight ? { height: pageHeight } : undefined;

	if (isLoading) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (isError) {
		return <ErrorPage code={(error as AxiosError)?.response?.status || 500} />;
	}

	if (!data || fullOrgData.employees.length === 0) {
		return <ErrorPage title="No org chart data" description="No employees were found for this company." />;
	}

	return (
		<div ref={pageRef} className={css.page} style={pageStyle}>
			<div ref={viewportRef} className={css.viewport} {...handlers}>
				<ChartCanvas
					contentRef={contentRef}
					contentWidth={contentWidth}
					contentHeight={contentHeight}
					visibleCycleGroups={visibleCycleGroups}
					visibleEmployees={visibleOrgData.employees}
					visibleEdges={visibleOrgData.edges}
					positions={positions}
					nodeInfo={nodeInfo}
					offsetX={offsetX}
					offsetY={offsetY}
					highlightedNodeId={highlightedNodeId}
					selectedNodeId={selectedNodeId}
					onNodeClick={handleNodeClick}
					onNodeToggle={toggleNode}
				/>

				<ChartToolbar
					onExpandAll={expandAll}
					onCollapseAll={collapseAll}
					onExportPng={handleExportPng}
					onChangeFields={canChangeFields ? handleChangeFields : undefined}
				/>

				<div data-interactive className={css.searchOverlay}>
					<SearchPanel allEmployees={fullOrgData.employees} onSelect={focusEmployee} />
					<BreadcrumbTrail reportingChain={reportingChain} onNavigate={focusEmployee} />
					{missingEmployeeId && (
						<div className={css.urlWarning}>Employee ID {missingEmployeeId} was not found.</div>
					)}
				</div>

				<ZoomControls onZoomIn={zoomIn} onZoomOut={zoomOut} onCenter={centerVisibleChart} />

				<Minimap
					nodes={layoutNodes}
					edges={visibleOrgData.edges}
					employeeMap={employeeMap}
					transform={transform}
					viewportWidth={viewportDimensions.width}
					viewportHeight={viewportDimensions.height}
					onNavigate={handleMinimapNavigate}
					highlightedNodeId={highlightedNodeId}
					selectedNodeId={selectedNodeId}
				/>
			</div>
		</div>
	);
}

export default OrgChart;
